import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import {
  DashboardRole,
  getCouponWindfallAnalysis,
  getDeliveryWindfallAnalysis,
} from '@shared/api/giftOrder/giftOrder.controller';
import {
  WindfallCouponResponse,
  WindfallDeliveryResponse,
  WindfallHistogramEntry,
  WindfallTypeSlice,
} from '@shared/api/giftOrder/giftOrderDashboard.interface';

import { fmtRate } from '../utils/dashboard.utils';
import { CenterLoading, EmptyBox, ErrorBox, StatCard } from './DashboardShared';

type SliceTab = 'coupon' | 'delivery';

interface FetchedData {
  coupon: WindfallTypeSlice;
  delivery: WindfallTypeSlice;
  periodLabel: string;
}

const EMPTY_SLICE: WindfallTypeSlice = {
  totalCount: 0,
  convertedCount: 0,
  windfallCount: 0,
  conversionRate: 0,
  avgDays: 0,
  medianDays: 0,
  minDays: 0,
  maxDays: 0,
  histogram: [],
};

function safeSlice(src?: WindfallTypeSlice): WindfallTypeSlice {
  if (!src) return EMPTY_SLICE;
  return { ...EMPTY_SLICE, ...src, histogram: src.histogram ?? [] };
}

function mergeSlices(a: WindfallTypeSlice, b: WindfallTypeSlice): WindfallTypeSlice {
  const totalCount = a.totalCount + b.totalCount;
  const convertedCount = a.convertedCount + b.convertedCount;
  const windfallCount = a.windfallCount + b.windfallCount;

  const dayMap = new Map<number, number>();
  for (const h of [...a.histogram, ...b.histogram]) {
    dayMap.set(h.day, (dayMap.get(h.day) ?? 0) + h.count);
  }
  const histogram = Array.from(dayMap.entries())
    .map(([day, count]) => ({ day, count }))
    .sort((x, y) => x.day - y.day);

  const allDays: number[] = [];
  for (const h of histogram) {
    for (let i = 0; i < h.count; i++) allDays.push(h.day);
  }
  allDays.sort((x, y) => x - y);

  let medianDays = 0;
  if (allDays.length > 0) {
    const mid = Math.floor(allDays.length / 2);
    medianDays =
      allDays.length % 2 === 1
        ? allDays[mid] ?? 0
        : ((allDays[mid - 1] ?? 0) + (allDays[mid] ?? 0)) / 2;
  }

  return {
    totalCount,
    convertedCount,
    windfallCount,
    conversionRate: totalCount > 0 ? convertedCount / totalCount : 0,
    avgDays:
      convertedCount > 0
        ? (a.avgDays * a.convertedCount + b.avgDays * b.convertedCount) / convertedCount
        : 0,
    medianDays,
    minDays: allDays[0] ?? 0,
    maxDays: allDays[allDays.length - 1] ?? 0,
    histogram,
  };
}

function calcPercentileDay(
  filled: WindfallHistogramEntry[],
  convertedCount: number,
  pct: number,
): number | null {
  if (convertedCount <= 0) return null;
  const threshold = Math.ceil(convertedCount * pct);
  let cumulative = 0;
  for (const h of filled) {
    cumulative += h.count;
    if (cumulative >= threshold) return h.day;
  }
  return null;
}

function toDateStr(value: unknown): string {
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (typeof value === 'string') return value.slice(0, 10);
  return String(value ?? '');
}

function buildPeriodLabel(
  couponRes: WindfallCouponResponse,
  deliveryRes: WindfallDeliveryResponse,
): string {
  const from = couponRes.period.from ?? deliveryRes.period.from;
  const to = couponRes.period.to ?? deliveryRes.period.to;
  if (!from && !to) return '전체 기간';
  return `${from ? toDateStr(from) : '~'} ~ ${to ? toDateStr(to) : '~'}`;
}

export const DashboardWindfallTab: FC<{
  from: string;
  to: string;
  role?: DashboardRole;
}> = ({ role }) => {
  const [data, setData] = useState<FetchedData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sliceTab, setSliceTab] = useState<SliceTab | 'all'>('all');

  const [rangeMode, setRangeMode] = useState<'all' | 'custom'>('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const fetchData = useCallback(
    async (from?: string, to?: string) => {
      setLoading(true);
      setError(null);
      try {
        const [couponRes, deliveryRes] = await Promise.all([
          getCouponWindfallAnalysis(from, to, role),
          getDeliveryWindfallAnalysis(from, to, role),
        ]);
        setData({
          coupon: safeSlice(couponRes.coupon),
          delivery: safeSlice(deliveryRes.delivery),
          periodLabel: buildPeriodLabel(couponRes, deliveryRes),
        });
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : '데이터를 불러올 수 없습니다.');
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [role],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApplyRange = useCallback(() => {
    if (rangeMode === 'all') {
      fetchData();
    } else if (customFrom && customTo) {
      fetchData(
        new Date(customFrom).toISOString(),
        new Date(customTo + 'T23:59:59.999Z').toISOString(),
      );
    }
  }, [rangeMode, customFrom, customTo, fetchData]);

  const handleResetToAll = useCallback(() => {
    setRangeMode('all');
    setCustomFrom('');
    setCustomTo('');
    fetchData();
  }, [fetchData]);

  const activeSlice = useMemo(() => {
    if (!data) return EMPTY_SLICE;
    if (sliceTab === 'coupon') return data.coupon;
    if (sliceTab === 'delivery') return data.delivery;
    return mergeSlices(data.coupon, data.delivery);
  }, [data, sliceTab]);

  if (loading) return <CenterLoading />;
  if (error) return <ErrorBox message={error} onRetry={() => fetchData()} />;
  if (!data) return <EmptyBox />;

  return (
    <div className="space-y-6">
      <section className="rounded-lg bg-white p-6 shadow">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-800">
              낙전 분석
              <span className="ml-2 text-xs font-normal text-gray-400">
                {data.periodLabel}
              </span>
            </h2>
            <p className="mt-1 text-xs text-gray-400">
              쿠폰·배송 아이템의 전환 소요일 분포를 분석합니다.
            </p>
          </div>
          <div className="flex rounded-md bg-gray-200 p-0.5 text-xs font-semibold">
            {(['all', 'coupon', 'delivery'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setSliceTab(t)}
                className={`rounded px-3 py-1 transition-colors ${
                  sliceTab === t
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t === 'all' ? '전체' : t === 'coupon' ? '쿠폰' : '배송'}
              </button>
            ))}
          </div>
        </div>

        {/* 기간 선택 UI */}
        <div className="mb-5 flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
          <div className="flex rounded-md bg-gray-200 p-0.5 text-xs font-semibold">
            <button
              onClick={() => setRangeMode('all')}
              className={`rounded px-3 py-1 transition-colors ${
                rangeMode === 'all'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              전체 기간
            </button>
            <button
              onClick={() => setRangeMode('custom')}
              className={`rounded px-3 py-1 transition-colors ${
                rangeMode === 'custom'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              기간 지정
            </button>
          </div>
          {rangeMode === 'custom' && (
            <>
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="rounded border border-gray-300 px-2 py-1 text-sm"
              />
              <span className="text-gray-400">~</span>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="rounded border border-gray-300 px-2 py-1 text-sm"
              />
              <button
                onClick={handleApplyRange}
                disabled={!customFrom || !customTo}
                className="rounded bg-indigo-500 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-indigo-600 disabled:opacity-40"
              >
                조회
              </button>
            </>
          )}
          {rangeMode === 'all' && data.periodLabel !== '전체 기간' && (
            <button
              onClick={handleResetToAll}
              className="text-xs text-indigo-500 hover:underline"
            >
              전체 기간으로 초기화
            </button>
          )}
        </div>

        {/* 요약 카드 */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard
            label="전환율"
            value={fmtRate(activeSlice.conversionRate)}
            sub={`${activeSlice.convertedCount} / ${activeSlice.totalCount}건`}
            accent="emerald"
          />
          <StatCard
            label="낙전 건수"
            value={`${activeSlice.windfallCount}건`}
            sub={`전체 ${activeSlice.totalCount}건 중`}
            accent="orange"
          />
          <StatCard
            label="평균 전환 소요일"
            value={`${activeSlice.avgDays.toFixed(1)}일`}
            sub={`중앙값 ${activeSlice.medianDays}일`}
            accent="indigo"
          />
          <StatCard
            label="전환 소요일 범위"
            value={`${activeSlice.minDays} ~ ${activeSlice.maxDays}일`}
            sub={activeSlice.minDays === 0 ? '당일 전환 포함' : undefined}
          />
        </div>

        {/* 히스토그램 */}
        <div className="mt-6">
          <h3 className="mb-3 text-sm font-semibold text-gray-700">전환 소요일 분포</h3>
          <Histogram
            histogram={activeSlice.histogram}
            convertedCount={activeSlice.convertedCount}
          />
        </div>

        {/* 전환/낙전 비율 바 */}
        <div className="mt-6">
          <h3 className="mb-3 text-sm font-semibold text-gray-700">전환 vs 낙전</h3>
          <RatioBar
            converted={activeSlice.convertedCount}
            windfall={activeSlice.windfallCount}
          />
        </div>
      </section>
    </div>
  );
};

/* ── 하위 컴포넌트 ── */

const Histogram: FC<{
  histogram: WindfallHistogramEntry[];
  convertedCount: number;
}> = ({ histogram, convertedCount }) => {
  if (histogram.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-400">
        전환 데이터가 없습니다.
      </div>
    );
  }

  const maxDay = histogram[histogram.length - 1]?.day ?? 0;
  const filled: WindfallHistogramEntry[] = [];
  let idx = 0;
  for (let d = 0; d <= maxDay; d++) {
    const entry = histogram[idx];
    if (idx < histogram.length && entry && entry.day === d) {
      filled.push(entry);
      idx++;
    } else {
      filled.push({ day: d, count: 0 });
    }
  }

  const maxCount = Math.max(...filled.map((h) => h.count), 1);
  const BAR_AREA_HEIGHT = 180;
  const barWidth = Math.min(Math.max(800 / filled.length, 10), 36);

  const p90Day = calcPercentileDay(filled, convertedCount, 0.9);
  const p98Day = calcPercentileDay(filled, convertedCount, 0.98);

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div
        className="flex items-end justify-center gap-0.5"
        style={{ height: BAR_AREA_HEIGHT + 24 }}
      >
        {filled.map((h) => {
          const ratio = h.count / maxCount;
          const barHeight = h.count > 0 ? Math.max(ratio * BAR_AREA_HEIGHT, 3) : 1;
          const isZeroDay = h.day === 0;
          const isP90 = p90Day !== null && h.day === p90Day;
          const isP99 = p98Day !== null && h.day === p98Day;
          return (
            <div
              key={h.day}
              className="group relative flex flex-col items-center justify-end"
              style={{ width: barWidth }}
            >
              <div className="pointer-events-none absolute -top-9 left-1/2 z-10 hidden -translate-x-1/2 whitespace-nowrap rounded bg-gray-800 px-2.5 py-1 text-sm text-white shadow group-hover:block">
                {h.day}일: {h.count}건
              </div>
              <div
                className={`w-full rounded-sm ${
                  isP99
                    ? 'bg-red-400'
                    : isP90
                    ? 'bg-orange-400'
                    : isZeroDay
                    ? 'bg-emerald-500'
                    : h.count > 0
                    ? 'bg-indigo-400'
                    : 'bg-gray-200'
                }`}
                style={{ height: barHeight }}
              />
              {h.count > 0 && barWidth >= 14 && (
                <span className="mt-0.5 text-[11px] font-semibold text-gray-600">
                  {h.count}
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-1 flex justify-center gap-0.5">
        {filled.map((h) => (
          <div
            key={h.day}
            className="text-center text-[11px] text-gray-400"
            style={{ width: barWidth }}
          >
            {barWidth >= 18 || h.day % Math.ceil(filled.length / 15) === 0 ? h.day : ''}
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between">
        <p className="text-xs text-gray-400">x축: 경과 일수 (0 = 당일)</p>
        <div className="flex items-center gap-4 text-xs font-medium">
          {p90Day !== null && (
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-orange-400" />
              <span className="text-orange-600">90% 전환: {p90Day}일</span>
            </span>
          )}
          {p98Day !== null && (
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-red-400" />
              <span className="text-red-500">98% 전환: {p98Day}일</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const RatioBar: FC<{ converted: number; windfall: number }> = ({
  converted,
  windfall,
}) => {
  const total = converted + windfall;
  if (total === 0) return null;
  const convertedPct = (converted / total) * 100;
  const windfallPct = (windfall / total) * 100;

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div className="flex h-8 overflow-hidden rounded-full">
        <div
          className="flex items-center justify-center bg-emerald-500 text-xs font-bold text-white transition-all"
          style={{ width: `${convertedPct}%` }}
        >
          {convertedPct >= 10 && `${convertedPct.toFixed(1)}%`}
        </div>
        <div
          className="flex items-center justify-center bg-amber-400 text-xs font-bold text-white transition-all"
          style={{ width: `${windfallPct}%` }}
        >
          {windfallPct >= 10 && `${windfallPct.toFixed(1)}%`}
        </div>
      </div>
      <div className="mt-2 flex justify-between text-xs">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-500" />
          <span className="text-gray-600">
            전환 <span className="font-bold text-emerald-700">{converted}건</span>
          </span>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-400" />
          <span className="text-gray-600">
            낙전 <span className="font-bold text-amber-700">{windfall}건</span>
          </span>
        </span>
      </div>
    </div>
  );
};
